import { useEffect, useState, useCallback } from "react";
import { Form, InputNumber, Select, Input, Upload, Button, Descriptions } from "antd";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons"

import { uploadFile } from "../../api/file";

import { cn, compressImage, formatDate } from "../../lib/utils";
import { UserRole, RateBy, LicenceArray, TicketArray } from "../../lib/constants";

import AttachmentModal from "../shared/AttachmentModal";

export default function UserForm({ onFormInstanceReady, isEditing }) {
  const [form] = Form.useForm();
  const [showAttachmentsModal, setShowAttachementsModal] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [viewAttachments, setViewAttachments] = useState([]);
  const [editing, setEditing] = useState(() => isEditing);

  const openAttachmentsModal = () => {
    setShowAttachementsModal(true);
  }

  const closeAttachmentsModal = () => {
    setShowAttachementsModal(false);
  }

  useEffect(() => {
    onFormInstanceReady(form);
    setAttachments(form.getFieldValue("attachments") || [])
  }, [form, onFormInstanceReady]);

  const updateViewAttachments = useCallback(() => {
    const header = [
      {
        key: "1",
        label: "Attachments",
        labelStyle: { border: "0" },
        contentStyle: { backgroundColor: "rgba(0, 0, 0, 0.02)" },
        span: 3,
      },
    ];

    attachments.map((attachment, index) => {
      let expireAt = attachment.fileAttachment.expireAt;
      if (typeof expireAt === "string") {
        expireAt = formatDate(new Date(expireAt));
      } else {
        expireAt = attachment.fileAttachment.expireAt.format("YYYY-MM-DD");
      }
      return header.push(
        {
          key: `${attachment.id + index + 1}`,
          label: "Document Name",
          children: attachment.fileAttachment.name
        },
        {
          key: `${attachment.id + index + 2}`,
          label: "Expiry Date",
          children: expireAt,
        },
        {
          key: `${attachment.id + index + 3}`,
          label: "Document",
          children: attachment.fileAttachment.file.name
        }
      );
    });
    setViewAttachments(header);
  }, [attachments]);

  useEffect(() => {
    updateViewAttachments();
  }, [updateViewAttachments, attachments]);

  const uploadProps = {
    beforeUpload: async (file) => {
      const compressedFile = await compressImage(file);
      return compressedFile;
    },
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        const res = await uploadFile(file, { entity: "user" });
        onSuccess(res);
      } catch (err) {
        onError(err);
      }
    },
    listType: "picture-card",
    multiple: false,
    maxCount: 1,
    accept: "image/*",
  }

  return (
    <div>
      <Form form={form} layout="vertical" className="w-1/2">
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item
            name={"avatar"}
            label="Upload Profile Picture"
            valuePropName="fileList"
            getValueFromEvent={(e) => e.fileList}
            className="col-span-2"
          >
            <Upload {...uploadProps}>
              <PlusOutlined className="mr-1" />
              Upload
            </Upload>
          </Form.Item>

          <Form.Item name={"firstName"} label="First Name" required>
            <Input />
          </Form.Item>

          <Form.Item name={"lastName"} label="Last Name">
            <Input />
          </Form.Item>

          <Form.Item name={"role"} label="Role" required>
            <Select
              placeholder="Select Role"
              options={Object.keys(UserRole).map((role) => {
                return {
                  value: UserRole[role],
                  label: role,
                };
              })}
            />
          </Form.Item>

          <Form.Item name={"email"} label="Email Address" required>
            <Input type="email" />
          </Form.Item>

          <Form.Item name={"phone"} label="Phone">
            <Input />
          </Form.Item>
          <div />

          <Form.Item name={'license'} label="License">
            <Select placeholder='Select License Type'
              options={LicenceArray}
            />
          </Form.Item>

          <Form.Item name={'ticket'} label="Ticket">
            <Select mode="multiple" placeholder='Select Ticket' allowClear
              options={TicketArray}
            />
          </Form.Item>

          <Form.Item name={"rate"} label="Rate" >
            <InputNumber addonBefore="AU$" defaultValue={0.00} min={0} step={"1.00"} className="w-full" />
          </Form.Item>

          <Form.Item name={"rateBy"} label="Rate by">
            <Select
              placeholder="Select Rate By"
              options={Object.keys(RateBy).map((rate) => {
                return {
                  value: RateBy[rate],
                  label: rate,
                };
              })}
            />
          </Form.Item>

          <Form.Item name={"password"} label="Password" className={cn(editing && 'hidden')} required>
            <Input.Password />
          </Form.Item>

          <Form.Item name={"confirm"} label="Confirm Password" className={cn(editing && 'hidden')} required>
            <Input.Password />
          </Form.Item>

          <Form.Item label="Attachments" value={attachments} name="attachments">
            <Button className="px-2" onClick={openAttachmentsModal} icon={<UploadOutlined />}> Click to Upload</Button>
          </Form.Item>
        </div>
      </Form >
      <AttachmentModal
        open={showAttachmentsModal}
        close={closeAttachmentsModal}
        entity={"user"}
        onSave={(values) => {
          form.setFieldValue("attachments", values)
          setAttachments(values)
        }}
        attachments={attachments}
      />
      <Descriptions bordered items={viewAttachments} className="pb-6" />

    </div>
  );
}
